<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-container">
      <div class="modal-header">
        <h3 class="modal-title">
          <span class="modal-icon">🔑</span>
          Change Password
        </h3>
        <button @click="close" class="modal-close">×</button>
      </div>

      <form @submit.prevent="changePassword" class="modal-body">
        <div class="form-group">
          <label for="old_password" class="form-label">Current Password</label>
          <div class="password-input-wrapper">
            <input
              id="old_password"
              v-model="passwordForm.old_password"
              :type="showOldPassword ? 'text' : 'password'"
              class="form-input"
              :class="{ 'error': errors.old_password }"
              placeholder="Enter current password"
              required
            />
            <button
              type="button"
              @click="showOldPassword = !showOldPassword"
              class="toggle-password"
            >
              <svg v-if="!showOldPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                <line x1="2" y1="2" x2="22" y2="22"/>
              </svg>
            </button>
          </div>
          <span v-if="errors.old_password" class="error-message">{{ errors.old_password }}</span>
        </div>

        <div class="form-group">
          <label for="new_password" class="form-label">New Password</label>
          <div class="password-input-wrapper">
            <input
              id="new_password"
              v-model="passwordForm.new_password"
              :type="showNewPassword ? 'text' : 'password'"
              class="form-input"
              :class="{ 'error': errors.new_password }"
              placeholder="Enter new password"
              required
            />
            <button
              type="button"
              @click="showNewPassword = !showNewPassword"
              class="toggle-password"
            >
              <svg v-if="!showNewPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                <line x1="2" y1="2" x2="22" y2="22"/>
              </svg>
            </button>
          </div>
          <span v-if="errors.new_password" class="error-message">{{ errors.new_password }}</span>

          <!-- Password Requirements -->
          <div class="password-requirements">
            <div class="requirement-item" :class="{ 'valid': passwordChecks.hasMinLength }">
              <span class="requirement-icon">{{ passwordChecks.hasMinLength ? '✓' : '×' }}</span>
              <span class="requirement-text">At least 8 characters long</span>
            </div>
            <div class="requirement-item" :class="{ 'valid': passwordChecks.hasUppercase }">
              <span class="requirement-icon">{{ passwordChecks.hasUppercase ? '✓' : '×' }}</span>
              <span class="requirement-text">At least one uppercase letter (A-Z)</span>
            </div>
            <div class="requirement-item" :class="{ 'valid': passwordChecks.hasLowercase }">
              <span class="requirement-icon">{{ passwordChecks.hasLowercase ? '✓' : '×' }}</span>
              <span class="requirement-text">At least one lowercase letter (a-z)</span>
            </div>
            <div class="requirement-item" :class="{ 'valid': passwordChecks.hasNumber }">
              <span class="requirement-icon">{{ passwordChecks.hasNumber ? '✓' : '×' }}</span>
              <span class="requirement-text">At least one number (0-9)</span>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="confirm_new_password" class="form-label">Confirm New Password</label>
          <div class="password-input-wrapper">
            <input
              id="confirm_new_password"
              v-model="passwordForm.confirm_new_password"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="form-input"
              :class="{ 'error': errors.confirm_new_password }"
              placeholder="Confirm new password"
              required
            />
            <button
              type="button"
              @click="showConfirmPassword = !showConfirmPassword"
              class="toggle-password"
            >
              <svg v-if="!showConfirmPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"/>
                <path d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"/>
                <line x1="2" y1="2" x2="22" y2="22"/>
              </svg>
            </button>
          </div>
          <span v-if="errors.confirm_new_password" class="error-message">{{ errors.confirm_new_password }}</span>
        </div>

        <div v-if="errorMessage" class="error-alert">
          {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="success-alert">
          {{ successMessage }}
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="close"
            class="btn-secondary"
            :disabled="isLoading"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn-primary"
            :disabled="isLoading || !isFormValid"
          >
            <span v-if="isLoading" class="loading-spinner-small"></span>
            <span v-else>✓</span>
            {{ isLoading ? 'Changing...' : 'Change Password' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChangePasswordModal',
  emits: ['close'],
  data () {
    return {
      passwordForm: {
        old_password: '',
        new_password: '',
        confirm_new_password: ''
      },
      errors: {},
      errorMessage: '',
      successMessage: '',
      isLoading: false,
      showOldPassword: false,
      showNewPassword: false,
      showConfirmPassword: false
    }
  },
  computed: {
    passwordChecks () {
      const password = this.passwordForm.new_password
      return {
        hasMinLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password)
      }
    },
    isPasswordValid () {
      const { hasMinLength, hasUppercase, hasLowercase, hasNumber } = this.passwordChecks
      return hasMinLength && hasUppercase && hasLowercase && hasNumber
    },
    isFormValid () {
      return (
        this.passwordForm.old_password.trim() !== '' &&
        this.passwordForm.new_password.trim() !== '' &&
        this.passwordForm.confirm_new_password.trim() !== '' &&
        this.isPasswordValid &&
        this.passwordForm.new_password === this.passwordForm.confirm_new_password
      )
    }
  },
  methods: {
    async changePassword () {
      this.errors = {}
      this.errorMessage = ''
      this.successMessage = ''

      // Client-side validation
      if (!this.isPasswordValid) {
        this.errors.new_password = 'Password does not meet requirements'
        return
      }

      if (this.passwordForm.new_password !== this.passwordForm.confirm_new_password) {
        this.errors.confirm_new_password = 'Passwords do not match'
        return
      }

      this.isLoading = true

      try {
        const token = localStorage.getItem('auth_token')

        const response = await fetch('https://api.quali-d.com/change-password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(this.passwordForm)
        })

        const data = await response.json()

        if (!response.ok || !data.success) {
          // Handle errors
          if (data.errors) {
            // Validation errors
            this.errors = data.errors
            this.errorMessage = data.message || 'Validation failed'
          } else {
            this.errorMessage = data.message || 'Failed to change password'
          }
        } else {
          // Success
          this.successMessage = data.message || 'Password changed successfully'

          // Reset form
          this.passwordForm = {
            old_password: '',
            new_password: '',
            confirm_new_password: ''
          }

          // Close modal after 2 seconds
          setTimeout(() => {
            this.close()
          }, 2000)
        }
      } catch (error) {
        console.error('Error changing password:', error)
        this.errorMessage = 'An error occurred. Please try again.'
      } finally {
        this.isLoading = false
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-icon {
  font-size: 24px;
}

.modal-close {
  background: none;
  border: none;
  font-size: 32px;
  color: var(--text-secondary);
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  padding-right: 48px;
  border: 2px solid var(--border-secondary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary);
  transition: all 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.toggle-password {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  padding: 4px;
  border-radius: 4px;
  transition: opacity 0.2s;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-password:hover {
  opacity: 1;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 13px;
  margin-top: 6px;
}

/* Password Requirements */
.password-requirements {
  margin-top: 12px;
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-secondary);
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  color: #6b7280;
  transition: all 0.2s;
}

.requirement-item.valid {
  color: #16a34a;
}

.requirement-icon {
  font-weight: bold;
  font-size: 14px;
  width: 16px;
  display: inline-block;
  text-align: center;
}

.requirement-item.valid .requirement-icon {
  color: #16a34a;
}

.requirement-item:not(.valid) .requirement-icon {
  color: #ef4444;
}

.requirement-text {
  line-height: 1.4;
}

.error-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
}

.success-alert {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  min-width: 120px;
  justify-content: center;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  .modal-container {
    width: 95%;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>
